'use client';

import React, { useState, useEffect, useCallback } from 'react';
import styled from './index.module.scss';
import { useQuestionDispatch, useQuestionStore, QuestionProvider } from './QuestionProvider';
import QuestionBanner from './Banner';
import Answer from './Examination';
import Finshed from './Examination/Finshed';
import { useGlobal } from '@/utils/useGlobal';
import EfModal from '@/components/UI/Modal';
import Button from '@/components/UI/Button';
import wss from '@/utils/mqtt-client';
import { getWSSUrl } from '@/utils/countryRegion';
import topics from '@/utils/mqtt-client/crowd-funding-topics';
import { QStatusEnum } from '@/components/AppreciationDay/index.d';
import { loginUser } from '@/utils/cookie';
import { getIsLogin } from 'lowcode/utils/getIsLogin';
import QuestionsHistory from './History';
import { useEcoDispatch, useEcoStore } from 'lowcode/utils/context/ecocredits';
import { useRouter } from 'next/router';
import { textLang } from '../textLang';
import gtm from '@/utils/gtm';
import { message } from '@/components/UI/Message';
import { signupCookie } from '@/utils/cookies/signupCookie';
import { createPortal } from 'react-dom';
import LoginFormModal from '@/components/LoginFormModal';

interface QuestionsProps {
    id: string;
    isEditStatus: boolean;
    background: {
        image: string;
        mobileImage: string;
    };
    styleOption: any;
    loginType: 'modal' | 'page';
}

const Questions: React.FC<QuestionsProps> = ({ id, isEditStatus, background, styleOption, loginType = 'page' }) => {
    const { getSystemTime } = useGlobal();
    const { locale, asPath, route, pathname } = useRouter();
    const lang = textLang[locale as string] || textLang.us;
    const { questionInfo, question, loading, scenario: scn } = useQuestionStore();
    const dispatchEco = useEcoDispatch();
    const { inBlackList } = useEcoStore();
    const dispatch = useQuestionDispatch();
    const [showExam, setShowExam] = useState(false);
    const [showFinshed, setShowFinshed] = useState(false);
    const [showRules, setShowRules] = useState(false);
    const time = getSystemTime();
    const url = String(getWSSUrl(locale as string));
    const userId = loginUser()?.user?.userId;

    const [isLogin, setIsLogin] = useState(false);
    const [showHistory, setShowHistory] = useState(false);

    const [showLoginModal, setShowLoginModal] = useState(false);
    const [isClient, setIsClient] = useState(false);

    useEffect(() => {
        const isLogin2 = getIsLogin();
        setIsLogin(isLogin2);

        if (typeof window !== 'undefined') {
            setIsClient(true);
        }
    }, []);

    const { receiver, mqttUnsubscribe, mqttSubscribe } = wss({
        url,
        topics,
        locale,
        subscribes: ['updatePoints'],
        activityId: userId,
    });

    useEffect(() => {
        if (!receiver || !receiver.data) return;
        const { questionReward } = receiver.data;
        dispatchEco({
            type: 'changeRewardUserInfo',
            payload: { locale },
        });
        dispatch({
            type: 'changeReward',
            payload: {
                rewardCouponCount: questionReward.rewardCouponCount,
                rewardTotalPoints: questionReward.rewardTotalPoints,
            },
        });
    }, [receiver]);

    const handleBegin = (btnName: string) => {
        gtm.push({
            event: 'uaEvent',
            event_name: 'activity_button',
            page_name: pathname,
            list_name: 'Quiz',
            button_name: btnName,
            function: 'Answering',
        });
        if (!isLogin) {
            signupCookie?.({
                sector_name: btnText?.(),
                entry_position: 0,
            });
            if (loginType === 'modal') {
                gtm.push({
                    event: 'uaEvent',
                    event_name: 'activity_join_login',
                    button_name: `${lang.rules}_${btnName}`,
                });
                setShowLoginModal(true);
                return;
            }
            location.href = `/${locale}/login?redirect=${encodeURIComponent(`/${locale}/${asPath}#Question`)}`;
            return;
        }
        if (inBlackList) return message.error(lang.not_eligible);
        // 没有活动信息
        if (!questionInfo) return;
        const { questionGroupId, campaignId } = questionInfo;
        dispatch({
            type: 'getQuestionList',
            payload: {
                isEditStatus,
                campaignId,
                questionGroupId,
                locale,
                url: `/${locale}${route}`,
            },
        });
    };

    const handleClose = () => {
        dispatch({
            type: 'getQuestionInfo',
            payload: { isEditStatus, id, locale },
        });
        setShowExam(false);
    };

    const handleLookBack = () => {
        dispatch({
            type: 'changeIndex',
            payload: 0,
        });
        setShowFinshed(false);
    };

    const handleOpenHistory = () => {
        if (!isLogin) {
            signupCookie?.({
                sector_name: lang.view_log,
                entry_position: 0,
            });
            if (loginType === 'modal') {
                gtm.push({
                    event: 'uaEvent',
                    event_name: 'activity_join_login',
                    button_name: `${lang.rules}_${lang.view_log}`,
                });
                setShowLoginModal(true);
                return;
            }
            location.href = `/${locale}/login?redirect=${encodeURIComponent(`/${locale}/${asPath}#Question`)}`;
            return;
        }
        setShowHistory(true);
    };

    const btnText = useCallback(() => {
        if (!isLogin && questionInfo) {
            if (time < questionInfo.campaignStartTime || time > questionInfo.campaignEndTime) {
                return lang.no_start;
            }
            return lang.start;
        }
        switch (questionInfo?.answerStatus) {
            case QStatusEnum.FIRST:
            case QStatusEnum.NEXT:
                return lang.start;
            case QStatusEnum.PROCEED:
                return lang.next;
            case QStatusEnum.FINISH:
                return lang.finshed;
            default:
                return lang.no_start;
        }
    }, [questionInfo]);

    useEffect(() => {
        // 试题为空
        if (!question || question.length === 0) return;
        // 已开始作答
        if (showExam) return;
        // 判断当前作答序号
        let ansIndex = 0;
        for (const item of question) {
            if (item.defAnswer === '') break;
            ansIndex++;
        }
        // 已作答结束
        if (ansIndex >= question.length) {
            setShowFinshed(true);
        } else {
            dispatch({
                type: 'changeIndex',
                payload: ansIndex,
            });
        }
        setShowExam(true);
    }, [question]);

    useEffect(() => {
        if (id) {
            dispatch({
                type: 'getQuestionInfo',
                payload: { isEditStatus, id, locale },
            });
            dispatchEco({
                type: 'changeRewardUserInfo',
                payload: { locale },
            });
        }
        if (!id && isEditStatus) {
            dispatch({
                type: 'changeQuestionInfo',
                payload: {
                    answerStatus: 'FIRST',
                    campaignEndTime: new Date().getTime() + 1000000,
                    campaignId: '',
                    campaignName: '活动名称',
                    campaignStartTime: new Date().getTime() - 1000000,
                    description: '',
                    questionGroupId: '',
                    rewardTotalPoints: '',
                    ruleDescription: '特殊规则',
                    subTitle: '活动副标题',
                },
            });
        }
        if (isLogin) mqttSubscribe('updatePoints');
        return () => mqttUnsubscribe('updatePoints');
    }, [id]);

    if (!questionInfo) return <></>;

    return (
        <div
            className={`${styled.questions} ${scn ? scn : ''} GtmModuleData-section Member-Questions`}
            data-section-id={'Member-Questions'}
            data-section-title={'Member-Questions'}
        >
            <QuestionBanner showRules={() => setShowRules(true)} background={background} />
            {/* 答题弹窗 */}
            <Button
                className="textbtn"
                disabled={
                    time < questionInfo.campaignStartTime || time > questionInfo.campaignEndTime || (isLogin && questionInfo?.answerStatus === QStatusEnum.NONE)
                }
                aria-label={''}
                onClick={() => handleBegin(btnText())}
                loading={loading}
            >
                {btnText()}
            </Button>
            <Button type="link" className="historybtn" onClick={handleOpenHistory}>
                {lang.view_log} &gt;
            </Button>
            <EfModal
                className={`${styled.question_modal}`}
                open={showExam}
                visible={showExam}
                width={960}
                onCancel={handleClose}
                zIndex={999}
                style={styleOption}
            >
                {!showFinshed ? <Answer finshed={() => setShowFinshed(true)} /> : <Finshed lookBack={handleLookBack} />}
            </EfModal>
            {/* rules弹窗 */}
            <EfModal className={styled.rules_modal} open={showRules} visible={showRules} width={960} zIndex={1200} onCancel={() => setShowRules(false)}>
                <div className="rule-content">
                    <h5 className="rule-title">{lang.rules}</h5>
                    <div className="rule-item">{questionInfo.description}</div>
                </div>
            </EfModal>
            {showHistory && (
                <EfModal open={showHistory} visible={showHistory} width={960} onCancel={() => setShowHistory(false)}>
                    <QuestionsHistory modal show={showHistory} />
                </EfModal>
            )}
            {isClient &&
                showLoginModal &&
                createPortal(
                    <LoginFormModal
                        visible={showLoginModal}
                        onCancel={() => {
                            setShowLoginModal(false);
                        }}
                        type="passwordless"
                    />,
                    document.body,
                )}
        </div>
    );
};

const HQuestion: React.FC<QuestionsProps> = (props) => (
    <QuestionProvider>
        <Questions {...props} />
    </QuestionProvider>
);

export default HQuestion;
